<template>
  <div class="customers">
    <div class="page__header">
      <div class="page__title">{{ $t(`pageTitles.${"leads"}`) }}</div>
      <div class="page__right"> 
         
        <div class="selected-filters" v-if="selectedTeams.length > 0 || Object.keys(searchLeadForms).length > 0">
          <div class="selected-filters__filter" v-for="teamId in selectedTeams" :key="`team${teamId}`">
            <span>
              Team: {{
                (loggedUser.teams && loggedUser.teams.find(t => t.id == teamId))
                  ? loggedUser.teams.find(t => t.id == teamId).name
                  : 'Team ' + teamId
              }}
            </span>
            <div class="selected-filters__remove" @click="removeTeam(teamId)"></div>
          </div>

          <div class="selected-filters__filter" v-for="statusId in leadStatuses" :key="`status${statusId}`">
            <span>{{
              (searchLeadForms.customer_sub_statuses && searchLeadForms.customer_sub_statuses.find(item => item.id ==
                statusId))
                ? searchLeadForms.customer_sub_statuses.find(item => item.id == statusId).name
                : '...'
            }}</span>
            <div class="selected-filters__remove" @click="removeLeadStatus(statusId)"></div>
          </div>

          <div class="selected-filters__filter" v-for="categoryId in leadCategories" :key="`category${categoryId}`">
            <span>{{
              (searchLeadForms.branchCategories && searchLeadForms.branchCategories.find(item => item.id ==
                categoryId))
                ? searchLeadForms.branchCategories.find(item => item.id == categoryId)[`long_${language}`]
                : '...'
            }}</span>
            <div class="selected-filters__remove" @click="removeLeadCategory(categoryId)"></div>
          </div>

          <div class="selected-filters__filter" v-for="agentId in selectedAgents" :key="`agent${agentId}`">
            <span>
              {{
                (searchLeadForms.agents && searchLeadForms.agents.find(a => a.id == agentId))
                  ? searchLeadForms.agents.find(a => a.id == agentId).first_name + ' ' +
                  searchLeadForms.agents.find(a => a.id == agentId).last_name
                  : 'Agent ' + agentId
              }}
            </span>
            <div class="selected-filters__remove" @click="removeAgent(agentId)"></div>
          </div>

          <div class="selected-filters__filter" v-if="deadlineFrom || deadlineTo">
            <span>Deadline: <br>{{ formatDateRange(deadlineFrom, deadlineTo) }}</span>
            <div class="selected-filters__remove" @click="clearDeadline()"></div>
          </div>

          <div class="selected-filters__filter" v-if="zipFrom || zipTo">
            <span>{{ $t('searchCustomerFields.zip') }}: {{ formatRange(zipFrom, zipTo) }}</span>
            <div class="selected-filters__remove" @click="clearZip()"></div>
          </div>
        </div>
        <form class="search search--filter" @submit.prevent="searchLeads()">
          <div class="search__wrapper">
            <input class="search__search" ref="search" :class="{ 'search__search--active': searchKeywords }" type="text"
              v-model="searchKeywords" />
            <div class="search__icon"></div>
            <div class="selected-filters__filter search__placeholder" v-if="searchPlaceholder">
              <div class="search__placeholder-content">
                {{ searchPlaceholder }}
                <div class="selected-filters__remove" @click="removeSearch()"></div>
              </div>
            </div>
            <div class="search__splitter"></div>
          </div>
          <div class="search__filter" ref="filterToggler" @click="showSearchFilter = !showSearchFilter"></div>
          <SearchFilter v-if="showSearchFilter" :filterToggler="$refs.filterToggler" :searchType="'lead'"
            :toggleSearchFilter="(state) => {
              showSearchFilter = state;
            }
              " />
        </form> 

        <button v-if="$checkRolePermission('customer_create')" type="button" class="button button--default import-button"
          @click="$store.commit('setShowLeadImportModal', true)">
          Import Leads
        </button>

        <button v-if="$checkRolePermission('customer_create')" type="button" class="button button--default transfer-button"
          @click="openTransferModal" :disabled="selectedCustomerIds.length === 0">
          Transfer{{ selectedCustomerIds.length > 0 ? ` (${selectedCustomerIds.length})` : '' }}
        </button>

        <button v-if="$checkRolePermission('customer_create')" type="button" class="button button--default add-button"
          @click="$store.commit('setShowCustomerLeadModal', true)">
          <div class="button__icon"></div>
        </button>

      </div>
    </div>
    <div class="customers__content">
      <Loading v-if="leadsLoading" />
      <LeadsTable v-else :customers="leadCustomersPerPage.data || []" @refresh="getLeadCustomers" @selection-changed="handleSelectionChange" />
    </div>
    <Pagination :limit="3" :data="leadCustomersPerPage" @pagination-change-page="getResults"></Pagination>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import LeadsTable from "@/components/LeadsTable";
import Loading from "@/partials/Loading";
import SearchFilter from "@/partials/SearchFilter";

export default {
  name: "Leads",

  components: {
    LeadsTable,
    Loading,
    SearchFilter,
  },

  data() {
    return {
      searchPlaceholder: "",
      searchKeywords: "",
      showSearchFilter: false,
      selectedTeams: [],
      selectedAgents: [],
      leadStatuses: [],
      leadCategories: [],
      leadReferals: [],
      deadlineFrom: "",
      deadlineTo: "",
      zipFrom: "",
      zipTo: "",
      initialLoadDone: false,
      selectedCustomerIds: [],
    };
  },

  computed: {
    ...mapGetters([
      "leadCustomersPerPage",
      "leadsLoading",
      "searchLeadForms",
      "language",
      "loggedUser",
    ]),
  },

  watch: {
    "$route.query": {
      handler(query) {
        if (!query.page) {
          this.$router.replace({ query: { ...query, page: 1 } });
          return;
        }
        this.loadFiltersFromQuery(query);

        this.searchLeads();
      },
      deep: true,
      immediate: true
    },
  },

  async created() {
    const hasAccess = this.$checkRolePermission("customer_access") || this.$checkRolePermission("customer_show");

    if (!hasAccess) {
      this.$router.push("/dashboard");
      return;
    }

    try {
      const queryTeams = this.$route.query.teams ? this.$route.query.teams.split(",").map(Number) : [];

      if (queryTeams.length > 0) {
        await this.$store.dispatch("getSearchLeadForms", queryTeams);
      } else if (this.loggedUser.teams && this.loggedUser.teams.length === 1) {
        await this.$store.dispatch("getSearchLeadForms", [this.loggedUser.teams[0].id]);
      }
    } catch (error) {
      console.error("Error loading lead forms:", error);
    }
  },

  methods: {
    formatDate(dateString) {
      if (!dateString) return null;
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);

      return `${day}/${month}/${year}`;
    },

    formatDateRange(from, to) {
      if (!from && !to) return '';
      if (from && !to) return `${this.formatDate(from)} - ...`;
      if (!from && to) return `... - ${this.formatDate(to)}`;
      return `${this.formatDate(from)} - ${this.formatDate(to)}`;
    },

    formatRange(from, to) {
      if (!from && !to) return '';
      if (from && !to) return `${from} - ...`;
      if (!from && to) return `... - ${to}`;
      return `${from} - ${to}`;
    },

    loadFiltersFromQuery(q) {
      this.searchKeywords = q.search || "";
      this.zipFrom = q.zip_from || "";
      this.zipTo = q.zip_to || "";

      if (q.agents) {
        this.selectedAgents = typeof q.agents === 'string' ? q.agents.split(",") : q.agents;
      } else {
        this.selectedAgents = [];
      }

      this.leadStatuses = q.lead_statuses
        ? (typeof q.lead_statuses === 'string' ? q.lead_statuses.split(",").map(Number) : q.lead_statuses)
        : [];

      this.leadCategories = q.lead_categories
        ? (typeof q.lead_categories === 'string' ? q.lead_categories.split(",").map(Number) : q.lead_categories)
        : [];

      this.leadReferals = q.lead_referrals
        ? (typeof q.lead_referrals === 'string' ? q.lead_referrals.split(",").map(Number) : q.lead_referrals)
        : [];

      this.deadlineFrom = q.deadline_from || "";
      this.deadlineTo = q.deadline_to || "";
    },

    async searchLeads() {
      this.$store.commit("setLeadsLoading", true);

      const page = Number(this.$route.query.page) || 1;

      const teamsFromUrl = this.$route.query.teams;
      const teams = teamsFromUrl ? teamsFromUrl.split(",").map(Number) : [];

      await this.$store.dispatch("searchLeads", {
        search: this.searchKeywords,
        agents: this.selectedAgents,
        lead_statuses: this.leadStatuses,
        lead_categories: this.leadCategories,
        lead_referrals: this.leadReferals,
        deadline_from: this.deadlineFrom,
        deadline_to: this.deadlineTo,
        zip_from: this.zipFrom,
        zip_to: this.zipTo,
        teams,
        page,
      });

      this.$store.commit("setLeadsLoading", false);
    },

    updateQuery() {
      this.$router.replace({
        query: {
          page: 1,
          search: this.searchKeywords || undefined,
          teams: this.selectedTeams.length ? this.selectedTeams.join(",") : undefined,
          agents: this.selectedAgents.length ? this.selectedAgents.join(",") : undefined,
          lead_statuses: this.leadStatuses.length ? this.leadStatuses.join(",") : undefined,
          lead_categories: this.leadCategories.length ? this.leadCategories.join(",") : undefined,
          lead_referrals: this.leadReferals.length ? this.leadReferals.join(",") : undefined,
          deadline_from: this.deadlineFrom || undefined,
          deadline_to: this.deadlineTo || undefined,
          zip_from: this.zipFrom || undefined,
          zip_to: this.zipTo || undefined,
        },
      }).catch(() => { });
    },

    getResults(paginationPage) {
      if (Number(this.$route.query.page) === Number(paginationPage)) return;

      this.$router.replace({
        query: { ...this.$route.query, page: paginationPage },
      }).catch(() => { });
    },

    clearDeadline() {
      this.deadlineFrom = "";
      this.deadlineTo = "";
      this.updateQuery();
    },

    clearZip() {
      this.zipFrom = "";
      this.zipTo = "";
      this.updateQuery();
    },

    removeTeam(id) {
      this.selectedTeams = this.selectedTeams.filter((x) => x !== id);

      if (this.selectedTeams.length === 0) {
        this.clearAllDependentFilters();
      }

      this.updateQuery();
    },

    clearAllDependentFilters() {
      this.selectedAgents = [];
      this.leadStatuses = [];
      this.leadCategories = [];
      this.leadReferals = [];
      this.deadlineFrom = "";
      this.deadlineTo = "";
      this.zipFrom = "";
      this.zipTo = "";

      this.$store.commit("setSearchLeadForms", {});
    },

    removeAgent(id) {
      this.selectedAgents = this.selectedAgents.filter((x) => x !== id);
      this.updateQuery();
    },

    removeLeadStatus(id) {
      this.leadStatuses = this.leadStatuses.filter((x) => x !== id);
      this.updateQuery();
    },

    removeLeadCategory(id) {
      this.leadCategories = this.leadCategories.filter((x) => x !== id);
      this.updateQuery();
    },

    removeSearch() {
      this.searchKeywords = "";
      this.searchPlaceholder = "";
      this.updateQuery();
    },

    async getLeadCustomers() {
      try {
        const page = Number(this.$route.query.page) || 1;
        await this.$store.dispatch("fetchLeadCustomers", page);
      } catch (error) {
        console.error("Failed to fetch leads:", error);
      }
    },
    
    handleSelectionChange(selectedIds) { 
      this.selectedCustomerIds = selectedIds.map(id => parseInt(id, 10));
    },
    
    openTransferModal() {
      if (this.selectedCustomerIds.length === 0) {
        return;
      }
      this.$store.commit('setLeadsToTransfer', this.selectedCustomerIds);
      this.$store.commit('setShowTransferLeadsModal', true);
    },
  },
};
</script>

<style scoped lang="scss">
.add-button {
  width: 70px !important;

  .button__icon {
    margin: auto;
    width: 24px;
    height: 24px;
    background-image: url("../assets/icons/default/add_icon.svg");
    background-repeat: no-repeat;
    background-position: center;
    transition: all 0.2s ease-in-out;
  }
}

.import-button{
  width: 150px !important;
}

.transfer-button{
  width: 150px !important;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.customers {
  height: 100%;

  &__content {
    margin-bottom: 20px;
    height: calc(100% - 48px - 15px - 16px - 20px);
  }
}
</style>